import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { linkDevice, searchVehicleByPlate } from '../../api/telematics';

const CARRIERS = ['orange', 'mtn', 'moov', 'other'];

export default function LinkDeviceModal({ open, onClose, onSaved, presetImei = '' }) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [imei, setImei] = useState(presetImei);
  const [simNumber, setSimNumber] = useState('');
  const [carrier, setCarrier] = useState('orange');
  const [installOdometerKm, setInstallOdometerKm] = useState('');
  const [plate, setPlate] = useState('');
  const [vehicle, setVehicle] = useState(null);
  const [searched, setSearched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Reset all fields each time the modal opens — it stays mounted between opens,
  // so without this a second link/reassign would show the previous device's data.
  useEffect(() => {
    if (open) {
      setImei(presetImei);
      setSimNumber('');
      setCarrier('orange');
      setInstallOdometerKm('');
      setPlate('');
      setVehicle(null);
      setSearched(false);
      setBusy(false);
      setError(null);
    }
  }, [open, presetImei]);

  if (!open) return null;

  const doSearch = async () => {
    setError(null); setSearched(false); setVehicle(null);
    try {
      const res = await searchVehicleByPlate(plate.trim(), token);
      setSearched(true);
      if (res?.found && res.vehicle?._id) setVehicle(res.vehicle);
    } catch (e) {
      setError(t('telematics.modal.vehicleNotFound'));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!/^\d{15}$/.test(imei.trim())) { setError(t('telematics.errors.imeiInvalid')); return; }
    if (!vehicle?._id) { setError(t('telematics.errors.vehicleRequired')); return; }
    setBusy(true);
    try {
      await linkDevice({
        imei: imei.trim(),
        vehicleId: vehicle._id,
        simNumber: simNumber.trim() || undefined,
        carrier: carrier === 'other' ? undefined : t(`telematics.carriers.${carrier}`),
        installOdometerKm: installOdometerKm === '' ? undefined : Number(installOdometerKm),
      }, token);
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.message || t('telematics.errors.linkFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl space-y-3 text-gray-900">
        <h2 className="text-lg font-semibold">
          {presetImei ? t('telematics.modal.reassignTitle') : t('telematics.modal.linkTitle')}
        </h2>

        <label className="block text-sm">{t('telematics.modal.imei')}
          <input className="mt-1 w-full rounded border px-3 py-2" value={imei}
            onChange={(e) => setImei(e.target.value)} disabled={!!presetImei} required />
        </label>

        <label className="block text-sm">{t('telematics.modal.sim')}
          <input className="mt-1 w-full rounded border px-3 py-2" value={simNumber}
            onChange={(e) => setSimNumber(e.target.value)} placeholder="+225 …" />
        </label>

        <label className="block text-sm">{t('telematics.modal.carrier')}
          <select className="mt-1 w-full rounded border px-3 py-2" value={carrier}
            onChange={(e) => setCarrier(e.target.value)}>
            {CARRIERS.map((c) => <option key={c} value={c}>{t(`telematics.carriers.${c}`)}</option>)}
          </select>
        </label>

        <label className="block text-sm">{t('telematics.modal.installOdometer')}
          <input type="number" min="0" className="mt-1 w-full rounded border px-3 py-2"
            value={installOdometerKm} onChange={(e) => setInstallOdometerKm(e.target.value)} />
        </label>

        <div className="text-sm">{t('telematics.modal.plate')}
          <div className="mt-1 flex gap-2">
            <input className="w-full rounded border px-3 py-2" value={plate}
              onChange={(e) => setPlate(e.target.value)} />
            <button type="button" onClick={doSearch}
              className="rounded bg-gray-100 px-3 py-2 text-sm">{t('telematics.modal.searchVehicle')}</button>
          </div>
          {vehicle && (
            <p className="mt-1 text-green-700">
              {t('telematics.modal.vehicleFound')}: {vehicle.plateNumber} — {vehicle.brand} {vehicle.model}
            </p>
          )}
          {searched && !vehicle && (
            <p className="mt-1 text-amber-700">{t('telematics.modal.vehicleNotFound')}</p>
          )}
        </div>

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded px-4 py-2 text-sm">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={busy}
            className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50">
            {busy ? t('common.loading') : t('telematics.modal.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
